import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { faqItems } from '@/mocks/faq';

function CategorySection() {
  return (
    <section className="py-16 md:py-20 px-6 md:px-10 bg-background-50">
      <div className="mx-auto" style={{ maxWidth: '1200px' }}>
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-3xl font-semibold text-foreground-950 font-[var(--font-heading)]">
            探索内容板块
          </h2>
          <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
            六大核心板块，全方位覆盖男同群体的需求，总有一个适合你
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {categories.map((cat) => (
            <Link
              key={cat.id}
              to={`/category/${cat.slug}`}
              className="group p-6 rounded-xl bg-background-100 hover:bg-primary-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300 flex items-start gap-4"
            >
              <div className="w-12 h-12 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0 group-hover:bg-primary-200 transition-colors">
                <i className={`${cat.icon} text-xl text-primary-500`}></i>
              </div>
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-foreground-950 font-[var(--font-heading)] whitespace-nowrap">
                  {cat.name}
                </h3>
                <p className="mt-1 text-sm text-foreground-600 leading-relaxed line-clamp-2">
                  {cat.description}
                </p>
                <p className="mt-2 text-xs text-foreground-500">
                  {cat.articleCount} 篇文章
                </p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function FeaturedArticles() {
  const featured = articles.filter((a) => a.featured).slice(0, 3);
  return (
    <section className="py-16 md:py-20 px-6 md:px-10 bg-background-100">
      <div className="mx-auto" style={{ maxWidth: '1200px' }}>
        <div className="flex items-center justify-between mb-10">
          <div>
            <h2 className="text-2xl md:text-3xl font-semibold text-foreground-950 font-[var(--font-heading)]">
              精选文章
            </h2>
            <p className="mt-1 text-sm text-foreground-600">
              编辑精选，为你推荐最值得阅读的内容
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {featured.map((article) => (
            <Link
              key={article.id}
              to={`/article/${article.id}`}
              className="group rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 overflow-hidden transition-all duration-300 flex flex-col"
            >
              <div className="relative h-48 overflow-hidden">
                <img
                  src={article.imageUrl}
                  alt={article.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-5 flex flex-col flex-1">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xs px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 font-medium whitespace-nowrap">
                    {article.categoryName}
                  </span>
                  <span className="text-xs text-foreground-500 whitespace-nowrap">
                    {article.readTime} 分钟阅读
                  </span>
                </div>
                <h3 className="text-base font-semibold text-foreground-950 leading-snug line-clamp-2 font-[var(--font-heading)]">
                  {article.title}
                </h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-2 flex-1">
                  {article.excerpt}
                </p>
                <div className="mt-4 flex items-center gap-2 pt-3 border-t border-background-200/70">
                  <img
                    src={article.authorAvatar}
                    alt={article.author}
                    className="w-6 h-6 rounded-full object-cover"
                  />
                  <span className="text-xs text-foreground-600">{article.author}</span>
                  <span className="ml-auto text-xs text-foreground-500">{article.publishDate}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function LatestArticles() {
  const latest = articles.filter((a) => !a.featured).slice(0, 6);
  return (
    <section className="py-16 md:py-20 px-6 md:px-10 bg-background-50">
      <div className="mx-auto" style={{ maxWidth: '1200px' }}>
        <div className="flex items-center justify-between mb-10">
          <div>
            <h2 className="text-2xl md:text-3xl font-semibold text-foreground-950 font-[var(--font-heading)]">
              最新资讯
            </h2>
            <p className="mt-1 text-sm text-foreground-600">
              持续更新，不错过任何重要信息
            </p>
          </div>
          <Link
            to="/category/zixun"
            className="hidden sm:flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap transition-colors"
          >
            查看全部 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {latest.map((article) => (
            <Link
              key={article.id}
              to={`/article/${article.id}`}
              className="group flex gap-4 p-4 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-200 transition-all duration-300"
            >
              <div className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0">
                <img
                  src={article.imageUrl}
                  alt={article.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="min-w-0 flex flex-col justify-between">
                <div>
                  <span className="text-xs text-primary-500 font-medium whitespace-nowrap">
                    {article.categoryName}
                  </span>
                  <h3 className="mt-1 text-sm font-semibold text-foreground-950 leading-snug line-clamp-2 font-[var(--font-heading)]">
                    {article.title}
                  </h3>
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-xs text-foreground-500">{article.publishDate}</span>
                  <span className="text-xs text-foreground-400">&middot;</span>
                  <span className="text-xs text-foreground-500">{article.readTime} 分钟阅读</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function FaqSection() {
  const [openId, setOpenId] = useState<string | null>(null);

  const toggle = useCallback((id: string) => {
    setOpenId((prev) => (prev === id ? null : id));
  }, []);

  return (
    <section id="faq" className="py-16 md:py-20 px-6 md:px-10 bg-background-100">
      <div className="mx-auto" style={{ maxWidth: '800px' }}>
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-semibold text-foreground-950 font-[var(--font-heading)]">
            常见问题
          </h2>
          <p className="mt-2 text-sm text-foreground-600">
            快速了解男同网，找到你想要的答案
          </p>
        </div>
        <div className="flex flex-col gap-3">
          {faqItems.map((item) => (
            <div
              key={item.id}
              className="rounded-xl bg-background-50 border border-background-200/70 overflow-hidden"
            >
              <button
                onClick={() => toggle(item.id)}
                className="w-full flex items-center justify-between p-5 text-left transition-colors"
              >
                <span className="text-sm font-medium text-foreground-950 pr-4">
                  {item.question}
                </span>
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
                    openId === item.id
                      ? 'bg-primary-500 text-white rotate-45'
                      : 'bg-background-100 text-foreground-600'
                  }`}
                >
                  <i className="ri-add-line text-lg"></i>
                </div>
              </button>
              <div
                className={`transition-all duration-300 overflow-hidden ${
                  openId === item.id ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <p className="px-5 pb-5 text-sm text-foreground-600 leading-relaxed">
                  {item.answer}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function NewsletterSection() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [msg, setMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const honeypot = (fd.get('website_alt') as string || '').trim();
    if (honeypot) {
      setStatus('success');
      setMsg('订阅成功！感谢你的关注。');
      setEmail('');
      return;
    }
    fd.delete('website_alt');
    fd.set('email', email.trim());
    try {
      const resp = await fetch('https://readdy.ai/api/form/d9sn555lbrt1qcgaj6ag', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(fd as unknown as Record<string, string>).toString(),
      });
      const text = await resp.text();
      let parsed: Record<string, unknown> = { code: '' };
      try { parsed = JSON.parse(text); } catch { /* ignore */ }
      if (resp.ok && parsed.code === 'OK') {
        setStatus('success');
        setMsg('订阅成功！感谢你的关注。');
        setEmail('');
      } else {
        const serverMsg = (parsed?.meta as Record<string, string>)?.message || (parsed?.message as string) || text;
        setStatus('error');
        setMsg(typeof serverMsg === 'string' ? serverMsg : '订阅失败，请稍后重试。');
      }
    } catch {
      setStatus('error');
      setMsg('网络错误，请检查网络后重试。');
    }
  };

  return (
    <section className="py-16 md:py-20 px-6 md:px-10 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-primary-500/8 via-accent-500/5 to-primary-500/8"></div>
      <div className="relative mx-auto text-center" style={{ maxWidth: '600px' }}>
        <h2 className="text-2xl md:text-3xl font-semibold text-foreground-950 font-[var(--font-heading)]">
          订阅我们的资讯
        </h2>
        <p className="mt-3 text-sm text-foreground-600">
          第一时间获取男同生活资讯、健康指南和活动信息
        </p>
        <form
          onSubmit={handleSubmit}
          className="mt-6 flex flex-col sm:flex-row gap-3 justify-center"
          data-readdy-form="true"
        >
          <input
            type="email"
            name="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="输入你的邮箱地址"
            required
            className="px-4 py-3 rounded-full border border-background-300/60 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 w-full sm:w-80 transition-colors"
          />
          <input
            type="text"
            name="website_alt"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            readOnly
            className="absolute opacity-0 pointer-events-none -z-10"
          />
          <button
            type="submit"
            className="px-6 py-3 rounded-full bg-primary-500 text-white text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors"
          >
            立即订阅
          </button>
        </form>
        {status !== 'idle' && (
          <p className={`mt-3 text-sm ${status === 'success' ? 'text-primary-600' : 'text-accent-600'}`}>
            {msg}
          </p>
        )}
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Navbar />

      <main>
        {/* Hero */}
        <section className="relative w-full h-[520px] md:h-[640px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20abstract%20artistic%20composition%20with%20soft%20flowing%20shapes%20in%20warm%20coral%20rose%20and%20golden%20amber%20tones%20gentle%20gradient%20blending%20into%20light%20cream%20background%20dreamy%20atmospheric%20minimal%20modern%20design%20with%20subtle%20texture&width=1920&height=800&seq=hero-bg-01&orientation=landscape"
            alt="男同网 - 温暖包容的社群平台"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/15 to-black/35"></div>
          <div className="relative z-10 flex flex-col items-center justify-center h-full text-center px-6 w-full">
            <h1 className="text-3xl md:text-5xl font-bold text-white leading-tight font-[var(--font-heading)]">
              找到属于你的精彩生活圈
            </h1>
            <p className="mt-4 md:mt-5 text-base md:text-lg text-white/80 max-w-2xl leading-relaxed">
              男同网是专为男同群体打造的综合性平台，提供交友、社群互动、生活资讯、健康指南及活动信息。加入我们，找到志同道合的伙伴。
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Link
                to="/category/jiaoyou"
                className="px-8 py-3.5 rounded-full bg-primary-500 text-white text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors"
              >
                开始交友
              </Link>
              <Link
                to="/about"
                className="px-8 py-3.5 rounded-full bg-white/15 text-white text-sm font-medium whitespace-nowrap border border-white/30 hover:bg-white/25 transition-colors backdrop-blur-sm"
              >
                了解更多
              </Link>
            </div>

            {/* Stats */}
            <div className="mt-12 flex flex-wrap items-center justify-center gap-8 md:gap-14">
              {[
                { num: '10,000+', label: '社群成员' },
                { num: '170+', label: '优质文章' },
                { num: '6', label: '内容板块' },
                { num: '50+', label: '合作机构' },
              ].map((stat) => (
                <div key={stat.label} className="text-center">
                  <p className="text-2xl md:text-3xl font-bold text-white font-[var(--font-heading)]">
                    {stat.num}
                  </p>
                  <p className="text-sm text-white/70 mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-background-50 to-transparent"></div>
        </section>

        <CategorySection />
        <FeaturedArticles />
        <LatestArticles />
        <FaqSection />
        <NewsletterSection />
      </main>

      <Footer />

      {/* Schema.org JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: '男同网',
            alternateName: '男同社群交友与资讯平台',
            url: 'https://nantongwang.com',
            description:
              '男同网是专为男同群体打造的综合性平台，提供交友、社群互动、生活资讯、健康指南及活动信息。',
            potentialAction: {
              '@type': 'SearchAction',
              target: 'https://nantongwang.com/search?q={search_term_string}',
              'query-input': 'required name=search_term_string',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: '男同网',
            url: 'https://nantongwang.com',
            description:
              '男同网是专为男同群体打造的综合性平台，提供交友、社群互动、生活资讯、健康指南及活动信息。',
            sameAs: [],
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqItems.map((item) => ({
              '@type': 'Question',
              name: item.question,
              acceptedAnswer: {
                '@type': 'Answer',
                text: item.answer,
              },
            })),
          }),
        }}
      />
    </>
  );
}